import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronUp } from "lucide-react";
import { ApiError } from "@/lib/api";
import { confirmAsync } from "@/components/Toaster";
import { formatDateTime, formatDate } from "@/lib/format";
import {
  useDeleteTelemetrCredentials,
  useSetTelemetrCredentials,
  useTelemetrCredentials,
  useTestTelemetrCredentials,
} from "@/lib/channels";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { StatusBadge } from "@/components/ui/badge";
import { Alert } from "@/components/ui/alert";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";

export function TelemetrCredentialsSection() {
  const { t } = useTranslation();
  const creds = useTelemetrCredentials();
  const set = useSetTelemetrCredentials();
  const del = useDeleteTelemetrCredentials();
  const test = useTestTelemetrCredentials();
  const [apiKey, setApiKey] = useState("");
  const [label, setLabel] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [showReplaceForm, setShowReplaceForm] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await set.mutateAsync({
        api_key: apiKey.trim(),
        api_key_label: label.trim() || undefined,
      });
      setApiKey("");
      setLabel("");
      setShowReplaceForm(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : t("settings.integrations.error"));
    }
  };

  const c = creds.data;

  /* Quota: prefer GET response fields, fallback to ephemeral test.data. */
  const testData = test.data;
  const showQuota =
    testData?.ok &&
    testData.requests_used != null &&
    testData.requests_limit != null;

  /* Status value for the badge */
  const statusValue = c?.has_key
    ? c.last_test_ok === true
      ? "ok"
      : c.last_test_ok === false
        ? "failed"
        : "ok" /* has key but untested → show neutral "ok" badge */
    : "failed";

  return (
    <div className="space-y-6">
      <div>
        <p className="text-sm text-fg-muted">{t("settings.integrations.subtitle")}</p>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">{t("settings.integrations.telemetr_title")}</CardTitle>
            {c?.has_key && (
              <StatusBadge
                domain="telemetr"
                value={statusValue}
                label={t(`settings.integrations.status.${statusValue}`)}
                dot
              />
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {c?.has_key ? (
            <>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                <span className="text-fg-muted">{t("settings.integrations.field_label")}</span>
                <span>{c.api_key_label ?? "—"}</span>

                <span className="text-fg-muted">{t("settings.integrations.last_test")}</span>
                <span>
                  {c.last_test_at == null ? (
                    <span className="text-fg-muted">{t("settings.integrations.untested")}</span>
                  ) : (
                    formatDateTime(c.last_test_at)
                  )}
                </span>

                {c.last_test_ok === false && c.last_test_error && (
                  <>
                    <span className="text-fg-muted">{t("settings.integrations.error_label")}</span>
                    <span className="text-danger text-xs break-all">{c.last_test_error}</span>
                  </>
                )}
              </div>

              {showQuota && (
                <div className="rounded-lg border border-border bg-bg-primary/40 p-3 space-y-1 text-xs">
                  <p className="text-fg-muted font-medium">{t("channels.telemetr.quota.title")}</p>
                  <p>
                    {t("channels.telemetr.quota.requests")}:{" "}
                    <span className="font-mono">
                      {testData!.requests_used} / {testData!.requests_limit}
                    </span>
                  </p>
                  <p>
                    {t("channels.telemetr.quota.channels")}:{" "}
                    <span className="font-mono">
                      {testData!.channels_used} / {testData!.channels_limit}
                    </span>
                  </p>
                  {testData!.billing_end && (
                    <p className="text-fg-muted">
                      {t("channels.telemetr.quota.renews")}:{" "}
                      {formatDate(testData!.billing_end)}
                    </p>
                  )}
                </div>
              )}

              <div className="flex flex-wrap gap-2 pt-1">
                <Button
                  size="sm"
                  variant="secondary"
                  loading={test.isPending}
                  onClick={() => test.mutate()}
                >
                  {test.isPending
                    ? t("channels.telemetr.testing")
                    : t("channels.telemetr.test")}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowReplaceForm((v) => !v)}
                  iconRight={showReplaceForm ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                >
                  {t("settings.integrations.replace_key")}
                </Button>
                <Button
                  size="sm"
                  variant="danger"
                  loading={del.isPending}
                  onClick={async () => {
                    const yes = await confirmAsync({
                      title: t("channels.telemetr.delete_confirm"),
                      confirmKind: "danger",
                      confirmLabel: t("channels.telemetr.delete"),
                    });
                    if (yes) void del.mutate();
                  }}
                >
                  {t("channels.telemetr.delete")}
                </Button>
              </div>

              {showReplaceForm && (
                <form onSubmit={onSubmit} className="border-t border-border pt-4 space-y-3">
                  <ReplaceKeyFields
                    apiKey={apiKey}
                    label={label}
                    onApiKeyChange={setApiKey}
                    onLabelChange={setLabel}
                    isPending={set.isPending}
                  />
                  {error && (
                    <Alert tone="danger">{error}</Alert>
                  )}
                  <div className="flex justify-end gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setShowReplaceForm(false);
                        setError(null);
                      }}
                    >
                      {t("common.cancel")}
                    </Button>
                    <Button type="submit" size="sm" loading={set.isPending} disabled={!apiKey.trim()}>
                      {t("settings.integrations.save_key")}
                    </Button>
                  </div>
                </form>
              )}
            </>
          ) : (
            <>
              <p className="text-sm text-fg-muted">{t("channels.telemetr.empty")}</p>
              <form onSubmit={onSubmit} className="space-y-3">
                <ReplaceKeyFields
                  apiKey={apiKey}
                  label={label}
                  onApiKeyChange={setApiKey}
                  onLabelChange={setLabel}
                  isPending={set.isPending}
                />
                {error && (
                  <Alert tone="danger">{error}</Alert>
                )}
                <div className="flex justify-end">
                  <Button type="submit" loading={set.isPending} disabled={!apiKey.trim()}>
                    {t("settings.integrations.save_key")}
                  </Button>
                </div>
              </form>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/* --------------------------------------------------------- shared form fields */

function ReplaceKeyFields({
  apiKey,
  label,
  onApiKeyChange,
  onLabelChange,
  isPending,
}: {
  apiKey: string;
  label: string;
  onApiKeyChange: (v: string) => void;
  onLabelChange: (v: string) => void;
  isPending: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="grid sm:grid-cols-2 gap-3">
      <div className="space-y-1.5">
        <Label htmlFor="tm_key">{t("channels.telemetr.field.key")}</Label>
        <Input
          id="tm_key"
          type="password"
          required
          value={apiKey}
          onChange={(e) => onApiKeyChange(e.target.value)}
          placeholder="****"
          disabled={isPending}
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="tm_label">{t("channels.telemetr.field.label")}</Label>
        <Input
          id="tm_label"
          value={label}
          onChange={(e) => onLabelChange(e.target.value)}
          placeholder="primary"
          disabled={isPending}
        />
      </div>
    </div>
  );
}
